import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardMedia, CardContent,
  Button, Chip, CircularProgress, Pagination, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Alert, IconButton
} from '@mui/material';
import {
  CloudDownload, Refresh, CheckCircle, Sync, AccessTime,
  ErrorOutline, PlayCircle, Image as ImageIcon, Lock,
  CreditCard, Replay, FolderOpen, CalendarToday, Tag
} from '@mui/icons-material';

// 🔴 Context Import
import { useTemplate } from '../contexts/TemplateContext';

// मुख्य कंपोनेंट
const MyDownloads = ({ user = {} }) => {
  // 🔴 Context se API call nikal li
  const { fetchDownloads } = useTemplate();

  // --- Local States for Data ---
  const [downloads, setDownloads] = useState([]);
  const [error, setError] = useState(null);
  const [designEntriesCount, setDesignEntriesCount] = useState(0);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Timer & UI States
  const [countdown, setCountdown] = useState(60);
  const [isPaused, setIsPaused] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({ open: false, title: '', message: '', type: 'info' });

  // UseRef taki setInterval me current page ki sahi value mile
  const currentPageRef = useRef(1);

  // 🔴 API Call Function
  const loadData = useCallback(async (page = 1, isBackgroundRefresh = false) => {
    if (!isBackgroundRefresh) setLoading(true);

    const res = await fetchDownloads({ page, limit: 12 });

    if (res.success) {
      setDownloads(res.data.downloads || []);
      setPagination(res.data.pagination);
      setDesignEntriesCount(res.data.pagination?.totalItems || 0);
      setError(null);
    } else {
      setError(res.message);
    }
    
    if (!isBackgroundRefresh) setLoading(false);
  }, [fetchDownloads]);

  // Page Load or Page Change pe API hit hogi
  useEffect(() => {
    currentPageRef.current = currentPage;
    loadData(currentPage, false);
  }, [currentPage, loadData]);

  // 60-second Auto Refresh Logic (Ab ye page reload nahi karega, sirf data layega)
  useEffect(() => {
    if (isPaused) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Background me API call hogi
          loadData(currentPageRef.current, true);
          return 60;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isPaused, loadData]);

  const handleManualRefresh = () => {
    setCountdown(60);
    loadData(currentPage, false);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showDialog = (title, message, type = 'info') => {
    setDialogConfig({ open: true, title, message, type });
  };

  const closeDialog = () => setDialogConfig(prev => ({ ...prev, open: false }));

  // --- Payment & Action Handlers ---
  const handleDownload = async (url, filename, type) => {
    if (!url) return showDialog('Error', 'Download URL not available', 'error');
    
    let finalFilename = filename || 'netaji-design';
    if (!finalFilename.includes('.')) {
      finalFilename += type === 'video' ? '.mp4' : '.jpg';
    }

    try {
      // API call to mark as downloaded
      await fetch(`/api/mark-downloaded/${url}`, { method: 'POST' }); 
      
      const link = document.createElement('a');
      link.href = `/api/download-file?fileUrl=${encodeURIComponent(url)}&fileName=${encodeURIComponent(finalFilename)}`;
      link.download = finalFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
    }
  };

  const handleRetry = async (templateId) => {
    if (!templateId) return showDialog('Error', 'Template ID not found', 'error');
    const selectedProfileId = localStorage.getItem('selectedProfileId');
    if (!selectedProfileId) return window.location.href = '/';

    try {
      showDialog('Processing', 'Retrying your design generation...', 'info');
    } catch (err) {
      showDialog('Retry Failed', 'Failed to retry generation.', 'error');
    }
  };

  const initiatePayment = async (entryId, amount, paymentUrl) => {
    setIsPaused(true); 
    
    if (paymentUrl) {
      window.location.href = paymentUrl;
      return;
    }

    try {
      const options = {
        key: 'YOUR_RAZORPAY_KEY',
        amount: amount * 100,
        currency: 'INR',
        name: 'NetajiPoster',
        description: 'Design Purchase',
        handler: function (response) {
          showDialog('Success', 'Payment successful! Downloading...', 'success');
          setTimeout(() => handleManualRefresh(), 2000);
        },
        modal: {
          ondismiss: function() {
            setIsPaused(false); 
          }
        },
        theme: { color: '#00838f' }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      showDialog('Error', 'Payment failed to initiate.', 'error');
      setIsPaused(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f4f6f8', minHeight: '100vh', pb: 8 }}>
      
      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #0A1929 0%, #006064 100%)', 
        color: 'white', pt: 6, pb: 8, px: 2, textAlign: 'center' 
      }}>
        <Container maxWidth="md">
          <CloudDownload sx={{ fontSize: 60, mb: 2, color: '#4dd0e1' }} />
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            मेरे डाउनलोड / My Downloads
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.8 }}>
            आपके द्वारा जेनरेट किए गए सभी डिज़ाइन यहां देखें
            <br />View all your generated designs here
          </Typography>
        </Container>
      </Box>

      {/* Control Bar (Floating) */}
      <Container maxWidth="lg" sx={{ mt: -4, mb: 4, position: 'relative', zIndex: 2 }}>
        <Card sx={{ borderRadius: 3, boxShadow: 3, px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box display="flex" alignItems="center">
            <Sync sx={{ mr: 1, color: isPaused ? 'warning.main' : 'primary.main', animation: isPaused ? 'none' : 'spin 2s linear infinite' }} />
            <Typography variant="body2" fontWeight="500">
              {isPaused ? 'Auto refresh paused' : `Auto refresh in`}
              {!isPaused && <Chip label={`${countdown}s`} size="small" color="primary" sx={{ ml: 1, fontWeight: 'bold' }} />}
            </Typography>
          </Box>
          <Button variant="outlined" color="primary" startIcon={loading ? <CircularProgress size={20} /> : <Refresh />} onClick={handleManualRefresh} disabled={loading} sx={{ borderRadius: 8, textTransform: 'none' }}>
            {loading ? 'Refreshing...' : 'Refresh Now'}
          </Button>
        </Card>
      </Container>

      <Container maxWidth="lg">
        {error && <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>{error}</Alert>}

        {loading && downloads.length === 0 ? (
          <Box display="flex" justifyContent="center" py={10}>
             <CircularProgress size={50} color="primary" />
          </Box>
        ) : downloads && downloads.length > 0 ? (
          <>
            {/* Header Info */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} borderBottom="1px solid #e0e0e0" pb={2}>
              <Box>
                <Typography variant="h5" fontWeight="bold" color="text.primary">
                  मेरे डिज़ाइन / My Designs
                </Typography>
                <Typography variant="body2" color="text.secondary">Designs created by you</Typography>
              </Box>
              {designEntriesCount > 0 && (
                <Chip label={`Total: ${designEntriesCount}`} color="primary" variant="outlined" />
              )}
            </Box>

            {/* Grid */}
            <Grid container spacing={4}>
              {downloads.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <DownloadCard 
                    item={item} 
                    onDownload={handleDownload} 
                    onPay={initiatePayment} 
                    onRetry={handleRetry} 
                  />
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={6}>
                <Pagination 
                  count={pagination.totalPages} 
                  page={pagination.currentPage} 
                  onChange={handlePageChange}
                  color="primary" 
                  size="large" 
                />
              </Box>
            )}
          </>
        ) : (
          /* Empty State */
          <Box textAlign="center" py={10} bgcolor="white" borderRadius={4} boxShadow={1}>
            <FolderOpen sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              कोई डाउनलोड नहीं मिला / No Downloads Found
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={4}>
              आपने अभी तक कोई डिज़ाइन जेनरेट नहीं किया है। डिज़ाइन बनाना शुरू करें!
            </Typography>
            <Button variant="contained" size="large" href="/today-design" sx={{ borderRadius: 8, px: 4, bgcolor: '#00838f', '&:hover': { bgcolor: '#006064' } }}>
              डिज़ाइन बनाएं / Create Design
            </Button>
          </Box>
        )}
      </Container>

      {/* Message Dialog */}
      <Dialog open={dialogConfig.open} onClose={closeDialog} PaperProps={{ sx: { borderRadius: 3, minWidth: 300 } }}>
        <DialogTitle display="flex" alignItems="center" gap={1}>
          {dialogConfig.type === 'error' && <ErrorOutline color="error" />}
          {dialogConfig.type === 'success' && <CheckCircle color="success" />}
          {dialogConfig.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogConfig.message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2, pr: 2 }}>
          <Button onClick={closeDialog} variant="contained" sx={{ borderRadius: 4 }}>OK</Button>
        </DialogActions>
      </Dialog>
      
      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </Box>
  );
};

// --- इंडिविजुअल कार्ड कंपोनेंट ---
const DownloadCard = ({ item, onDownload, onPay, onRetry }) => {
  const isProcessing = item.status === 'processing';
  const isSubmitted = item.status === 'submitted';
  const isFailed = item.status === 'failed';
  const isReady = item.status === 'ready' || item.status === 'completed' || item.status === 'done';

  return (
    <Card sx={{ 
      height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 4, 
      boxShadow: '0 8px 24px rgba(0,0,0,0.06)', transition: 'transform 0.3s', 
      '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 12px 32px rgba(0,0,0,0.1)' } 
    }}>
      
      {/* Media & Status Area */}
      <Box position="relative" height={220} bgcolor="#eceff1" display="flex" alignItems="center" justifyContent="center" overflow="hidden">
        
        {/* Status Chip */}
        <Box position="absolute" top={12} right={12} zIndex={10}>
          {isReady && <Chip icon={<CheckCircle />} label="Ready" color="success" size="small" />}
          {isProcessing && <Chip icon={<Sync className="fa-spin" />} label="Processing" color="warning" size="small" />}
          {isSubmitted && <Chip icon={<AccessTime />} label="In Queue" color="info" size="small" />}
          {isFailed && <Chip icon={<ErrorOutline />} label="Failed" color="error" size="small" />}
        </Box>

        {/* Dynamic Media rendering */}
        {isProcessing || isSubmitted ? (
          <Box textAlign="center" zIndex={2}>
            <CircularProgress variant={item.progressPercentage ? "determinate" : "indeterminate"} value={item.progressPercentage || 0} size={70} thickness={4} color={isProcessing ? "warning" : "info"} />
            <Typography variant="h6" fontWeight="bold" mt={2} color={isProcessing ? "warning.main" : "info.main"}>
              {item.progressPercentage || 0}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isProcessing ? 'Crafting design...' : 'Waiting in queue...'}
            </Typography>
          </Box>
        ) : (
          <>
            {item.isVideo ? (
              <CardMedia component="video" src={item.finalUrl} autoPlay loop muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <CardMedia component="img" image={item.finalUrl || item.thumbnail} alt={item.templateName} sx={{ height: '100%', objectFit: 'contain' }} />
            )}
            
            {/* Media Type Indicator */}
            <Chip 
              icon={item.isVideo ? <PlayCircle fontSize="small" /> : <ImageIcon fontSize="small" />} 
              label={item.isVideo ? "VIDEO" : "IMAGE"} 
              size="small" 
              sx={{ position: 'absolute', bottom: 12, left: 12, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', border: 'none' }} 
            />
          </>
        )}

        {/* Lock Overlay for Payment */}
        {item.paymentRequired && !isProcessing && !isSubmitted && (
          <Box position="absolute" top={0} left={0} right={0} bottom={0} bgcolor="rgba(255,255,255,0.4)" sx={{ backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box bgcolor="white" p={1.5} borderRadius={3} boxShadow={3} textAlign="center" color="warning.dark">
              <Lock sx={{ fontSize: 30, mb: 0.5 }} />
              <Typography variant="subtitle2" fontWeight="bold">Premium</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Content Area */}
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" fontWeight="bold" noWrap gutterBottom title={item.templateName || 'Custom Design'}>
          {item.templateName || 'Custom Design'}
        </Typography>
        
        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          <Chip icon={<Tag fontSize="small" />} label={item.id} size="small" variant="outlined" />
          <Chip icon={<CalendarToday fontSize="small" />} label={new Date(item.createdAt).toLocaleDateString()} size="small" variant="outlined" />
        </Box>

        {item.message && isFailed && (
            <Typography variant="caption" color="error" sx={{ mb: 2, display: 'block' }}>
                {item.message}
            </Typography>
        )}

        <Box mt="auto" pt={2} borderTop="1px solid #eee">
          {item.paymentRequired ? (
            <Box>
              <Box display="flex" justifyContent="space-between" mb={1.5} px={1}>
                <Typography variant="body2" color="text.secondary">Amount Due</Typography>
                <Typography variant="subtitle1" fontWeight="bold" color="error.main">
                  ₹{item.actualAmount || item.amount || 'Required'}
                </Typography>
              </Box>
              <Button fullWidth variant="contained" color="warning" startIcon={<CreditCard />} onClick={() => onPay(item.id, item.actualAmount || item.amount, item.paymentUrl)} sx={{ borderRadius: 6, fontWeight: 'bold' }}>
                Unlock & Pay Now
              </Button>
            </Box>
          ) : isReady && item.finalUrl ? (
            <Button fullWidth variant="contained" sx={{ bgcolor: '#00838f', '&:hover': { bgcolor: '#006064' }, borderRadius: 6 }} startIcon={<CloudDownload />} onClick={() => onDownload(item.finalUrl, item.templateName, item.isVideo ? 'video' : 'image')}>
              Download HQ
            </Button>
          ) : isProcessing ? (
            <Button fullWidth variant="contained" disabled sx={{ borderRadius: 6 }}>
              Generating...
            </Button>
          ) : isSubmitted ? (
            <Button fullWidth variant="contained" disabled sx={{ borderRadius: 6 }}>
              Queued...
            </Button>
          ) : isFailed ? (
            <Button fullWidth variant="outlined" color="error" startIcon={<Replay />} onClick={() => onRetry(item.id)} sx={{ borderRadius: 6 }}>
              Try Again
            </Button>
          ) : (
            <Button fullWidth variant="contained" disabled sx={{ borderRadius: 6 }}>Unavailable</Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MyDownloads;